import { useEffect, useRef, useState } from "react";
import ModalV2 from "../../ModalV2";
import Toggle from "../../Toggle";
import Tooltip from "../../Tooltip";
import TagInput from "../../TagInput";
import useModal from "../../../hooks/useModal";
import useAPI from "../../../hooks/useAPI";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { Upload, X } from "lucide-react";
import PropTypes from "prop-types";

export default function UpsertSmartContractModal({ onUpsert, networks, abis = {} }) {
  const api = useAPI();
  const { close, data } = useModal("create-smart-contract-modal");
  const fileInputRef = useRef(null);
  const [abiSource, setAbiSource] = useState("");
  const [customAbi, setCustomAbi] = useState(null);
  const [customAbiName, setCustomAbiName] = useState("");
  const [abiError, setAbiError] = useState("");
  const abiKeys = Object.keys(abis);
  const [contractData, setContractData] = useState({
    name: "",
    description: "",
    symbol: "",
    address: "",
    decimals: "",
    networkId: "",
    requiresKyc: false,
    enabled: true,
    displayDefault: true,
    buyable: false,
    logo: "",
    tags: [],
  });
  const [fixedPrice, setFixedPrice] = useState("");
  const [fixedPriceTouched, setFixedPriceTouched] = useState(false);
  const [purchaseFee, setPurchaseFee] = useState("");
  const [purchaseFeeTouched, setPurchaseFeeTouched] = useState(false);

  const fixedPriceValid = fixedPrice === "" || /^\d+(\.\d{1,18})?$/.test(fixedPrice);
  const purchaseFeeValid =
    purchaseFee === "" ||
    (/^\d+(\.\d{1,18})?$/.test(purchaseFee) && Number(purchaseFee) <= 1);


  useEffect(() => {
    if (data) {
      setContractData({
        name: data.name || "",
        description: data.description || "",
        symbol: data.symbol || "",
        address: data.address || "",
        decimals: data.decimals || "",
        networkId: data.networkId || "",
        requiresKyc: data.requiresKyc !== undefined ? data.requiresKyc : false,
        enabled: data.enabled !== undefined ? data.enabled : true,
        displayDefault:
          data.displayDefault !== undefined ? data.displayDefault : true,
        buyable: data.buyable !== undefined ? data.buyable : false,
        logo: data.logo || "",
        tags: Array.isArray(data.tags) ? data.tags : [],
      });
      const initialAbi = data.abiURL || "";
      setAbiSource(typeof initialAbi === "string" ? (abis[initialAbi] || initialAbi) : "");
      setCustomAbi(typeof initialAbi === "object" ? initialAbi : null);
      setCustomAbiName("");
      setAbiError("");
      setFixedPrice(
        data.fixedPrice === null || data.fixedPrice === undefined
          ? ""
          : String(data.fixedPrice)
      );
      setFixedPriceTouched(false);
      setPurchaseFee(
        data.purchaseFee === null || data.purchaseFee === undefined
          ? ""
          : String(data.purchaseFee)
      );
      setPurchaseFeeTouched(false);
    } else {
      setContractData({
        name: "",
        description: "",
        symbol: "",
        address: "",
        decimals: "",
        networkId: "",
        requiresKyc: false,
        enabled: true,
        displayDefault: true,
        buyable: false,
        logo: "",
        tags: [],
      });
      setAbiSource("");
      setCustomAbi(null);
      setCustomAbiName("");
      setAbiError("");
      setFixedPrice("");
      setFixedPriceTouched(false);
      setPurchaseFee("");
      setPurchaseFeeTouched(false);
    }
  }, [data, abis]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        setCustomAbi(parsed);
        setCustomAbiName(file.name);
        setAbiSource("custom");
        setAbiError("");
      } catch {
        setAbiError("Invalid JSON file");
        setCustomAbi(null);
        setCustomAbiName("");
      }
    };
    reader.readAsText(file);
  };

  const getAbiURL = () => {
    if (abiSource === "custom") return customAbi;
    return abiSource || null;
  };

  const handleCreateContract = async () => {
    if (!fixedPriceValid) {
      toast.error("Fixed price must be a non-negative number with up to 18 decimal places");
      return;
    }
    if (!purchaseFeeValid) {
      toast.error("Purchase fee must be a decimal fraction between 0 and 1 with up to 18 decimal places");
      return;
    }
    try {
      const payload = { ...contractData, abiURL: getAbiURL() };
      const isUpdate = Boolean(data?.id);
      if (!isUpdate || fixedPriceTouched) {
        payload.fixedPrice = fixedPrice === "" ? null : Number(fixedPrice);
      }
      if (!isUpdate || purchaseFeeTouched) {
        payload.purchaseFee = purchaseFee === "" ? null : Number(purchaseFee);
      }
      if (isUpdate) {
        await api.updateAdminSmartContract(data?.id, payload);
        toast.success("Smart contract updated successfully");
      } else {
        await api.createAdminSmartContract(payload);
        toast.success("Smart contract created successfully");
      }
      if (onUpsert) onUpsert();
      close();
    } catch (error) {
      toast.error(error.message || "Failed to save smart contract");
    }
  };

  return (
    <ModalV2 name={"create-smart-contract-modal"}>
      <div className="p-8 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-2">
            <h1 className="text-white text-3xl font-black leading-tight tracking-[-0.033em]">
              {data?.id ? "Edit Smart Contract" : "Add New Smart Contract"}
            </h1>
            <p className="text-blue-100 text-base font-normal">
              Configure token contract details and deployment settings.
            </p>
          </div>
          <button className="text-gray-400 hover:text-white transition-colors" onClick={() => close()}>
            <X />
          </button>
        </div>
      </div>
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {/* Column 1 */}
          <div className="space-y-6">
            <label className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="text-gray-900 text-sm font-medium">
                  Contract Name
                </p>
                <Tooltip text="The display name of the smart contract">
                  <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                    info
                  </span>
                </Tooltip>
              </div>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="e.g. USD Coin"
                value={contractData.name}
                onChange={(e) =>
                  setContractData({
                    ...contractData,
                    name: e.target.value,
                  })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Description</p>
              <textarea
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white placeholder:text-gray-400 px-4 py-3 text-base font-normal resize-none"
                placeholder="A brief description of the smart contract"
                rows="3"
                value={contractData.description}
                onChange={(e) =>
                  setContractData({ ...contractData, description: e.target.value })
                }
              />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-2">
                <p className="text-gray-900 text-sm font-medium">
                  Token Symbol
                </p>
                <input
                  className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                  placeholder="USDC"
                  value={contractData.symbol}
                  onChange={(e) =>
                    setContractData({ ...contractData, symbol: e.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-2">
                <p className="text-gray-900 text-sm font-medium">Decimals</p>
                <input
                  type="number"
                  className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                  placeholder="18"
                  value={contractData.decimals}
                  onChange={(e) =>
                    setContractData({ ...contractData, decimals: e.target.value })
                  }
                />
              </label>
            </div>
          </div>
          {/* Column 2 */}
          <div className="space-y-6">
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Contract Address</p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal font-mono"
                placeholder="0x..."
                value={contractData.address}
                onChange={(e) =>
                  setContractData({ ...contractData, address: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Network</p>
              <select
                className="w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 px-4 text-base font-normal"
                value={contractData.networkId}
                onChange={(e) =>
                  setContractData({
                    ...contractData,
                    networkId: e.target.value,
                  })
                }
              >
                <option value="">Select network</option>
                {networks.map((network) => (
                  <option key={network.id} value={network.id}>
                    {network.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Logo URL</p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="https://example.com/logo.png"
                value={contractData.logo}
                onChange={(e) =>
                  setContractData({ ...contractData, logo: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="text-gray-900 text-sm font-medium">Fixed price (USD)</p>
                <Tooltip text="Set a manual USD price for tokens without a price oracle. Leave empty to use the default price source.">
                  <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                    info
                  </span>
                </Tooltip>
              </div>
              <input
                type="text"
                inputMode="decimal"
                className={`form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal ${fixedPriceValid ? "border-gray-300" : "border-red-400"}`}
                placeholder="e.g. 0.045"
                value={fixedPrice}
                onChange={(e) => {
                  setFixedPrice(e.target.value);
                  setFixedPriceTouched(true);
                }}
              />
              <span className="text-xs text-gray-500">
                Set a manual USD price for tokens without a price oracle. Leave empty to use the default price source.
              </span>
              {!fixedPriceValid && (
                <span className="text-xs text-red-500">
                  Must be a non-negative number with up to 18 decimal places.
                </span>
              )}
            </label>
            <label className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="text-gray-900 text-sm font-medium">Purchase fee</p>
                <Tooltip text="Decimal fraction taken as a fee when this token is purchased (e.g. 0.025 = 2.5%). Leave empty for no fee.">
                  <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                    info
                  </span>
                </Tooltip>
              </div>
              <input
                type="text"
                inputMode="decimal"
                className={`form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal ${purchaseFeeValid ? "border-gray-300" : "border-red-400"}`}
                placeholder="e.g. 0.025"
                value={purchaseFee}
                onChange={(e) => {
                  setPurchaseFee(e.target.value);
                  setPurchaseFeeTouched(true);
                }}
              />
              <span className="text-xs text-gray-500">
                Decimal fraction (0–1). 0.025 = 2.5%. Leave empty for no fee.
              </span>
              {!purchaseFeeValid && (
                <span className="text-xs text-red-500">
                  Must be a decimal fraction between 0 and 1 with up to 18 decimal places.
                </span>
              )}
            </label>
          </div>
        </div>
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-gray-900 text-sm font-medium">ABI</p>
            <Tooltip text="Select a predefined ABI or upload a custom JSON file">
              <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                info
              </span>
            </Tooltip>
          </div>
          <select
            value={abiSource === "custom" ? "custom" : abiSource}
            onChange={(e) => {
              setAbiSource(e.target.value);
              if (e.target.value !== "custom") {
                setCustomAbi(null);
                setCustomAbiName("");
                setAbiError("");
              }
            }}
            className="w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 px-4 text-base font-normal"
          >
            <option value="">Select an ABI</option>
            {abiKeys.map((key) => (
              <option key={key} value={abis[key]}>{key}</option>
            ))}
            <option value="custom">Upload custom ABI...</option>
          </select>
          {abiSource === "custom" && (
            <div className="mt-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 w-full rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                <Upload size={14} />
                {customAbiName || (customAbi ? "Custom ABI loaded (choose a file to replace)" : "Choose a JSON file...")}
              </button>
              {abiError && <p className="text-xs text-red-500 mt-1">{abiError}</p>}
            </div>
          )}
          {abiSource && abiSource !== "custom" && !Object.values(abis).includes(abiSource) && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Current ABI URL</label>
              <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 break-all">
                {abiSource}
              </div>
            </div>
          )}
        </div>
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-gray-900 text-sm font-medium">Tags</p>
            <Tooltip text="Label this contract with badges like soulbound or faucet">
              <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                info
              </span>
            </Tooltip>
          </div>
          <TagInput
            value={contractData.tags}
            onChange={(tags) => setContractData({ ...contractData, tags })}
          />
        </div>
        {/* Toggles Section */}
        <div className="mt-10 pt-8 border-t border-gray-300 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">
                Requires KYC
              </span>
              <span className="text-gray-600 text-xs">
                Require user verification
              </span>
            </div>
            <Toggle
              checked={contractData.requiresKyc}
              onChange={(value) =>
                setContractData({ ...contractData, requiresKyc: value })
              }
              text="Requires KYC"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">
                Display by Default
              </span>
              <span className="text-gray-600 text-xs">
                Show in the primary list
              </span>
            </div>
            <Toggle
              checked={contractData.displayDefault}
              onChange={(value) =>
                setContractData({ ...contractData, displayDefault: value })
              }
              text="Display by Default"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">Enabled</span>
              <span className="text-gray-600 text-xs">
                Allow interactions
              </span>
            </div>
            <Toggle
              checked={contractData.enabled}
              onChange={(value) =>
                setContractData({ ...contractData, enabled: value })
              }
              text="Enabled"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">Buyable</span>
              <span className="text-gray-600 text-xs">
                Available for purchase
              </span>
            </div>
            <Toggle
              checked={contractData.buyable}
              onChange={(value) =>
                setContractData({ ...contractData, buyable: value })
              }
              text="Buyable"
            />
          </div>
        </div>
        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <div>
            <Button type="tertiary" onClick={() => close()}>
              Cancel
            </Button>
          </div>
          <div>
            <Button type="primary" onClick={handleCreateContract}>
              {data?.id ? "Update Contract" : "Create Contract"}
            </Button>
          </div>
        </div>
      </div>
    </ModalV2>
  );
}

UpsertSmartContractModal.propTypes = {
  onUpsert: PropTypes.func,
  networks: PropTypes.array.isRequired,
  abis: PropTypes.object,
};